import React, { Component } from 'react';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  Link,
  TextField,
  MenuItem,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { getSites } from '../../services/moderationDashboardService';
import {
  CANONICAL_MODES,
  modeLabel,
  REGIME_APPLIED,
  regimeLabel,
} from '../../lib/moderationModes';

// Onglet « Config par site » du dashboard Modération IA (B2_02, Volet B de l'EPIC).
//
// « Qui a l'IA, en quel mode, avec quel contexte éditorial, et où en est le quota. »
// Lecture seule : aucun bouton d'édition ici, le dashboard observe. L'édition vit ailleurs —
// les 9 champs `modere` dans le back-office CLIENT, `llm_ai_autonomous` dans le super-admin
// (Website.js) et les analyses incluses au forfait sur la licence (Formlicence, B6_01) — jamais
// dans le back-office. (`ai_allowed` n'a plus ni UI ni effet depuis B6_01 / D47.)
//
// Source unique : moderationDashboardService.getSites() → CD2 + CD2+ (endpoint B1_02).
// ⛔ Ce fichier ne touche PAS au service (CD4 : propriétaire exclusif B0_02).
//
// ─────────────────────────────────────────────────────────────────────────────
// LES DEUX FAUX AMIS DE CET ENDPOINT — à lire avant de toucher au rendu
//
// 1. `active` vaut `true` sur TOUTES les lignes. Ce n'est pas un état, c'est le prédicat de
//    la liste : CD2 ne renvoie que les sites qui ont réellement consommé (D4/D5). Un badge
//    « actif / inactif » branché dessus serait du code mort, et le mot « inactif » décrirait
//    en réalité `dormant_count`, qui n'est pas une ligne du tableau.
//    Le vrai signal d'actualité est `armed` : un site peut avoir consommé PUIS coupé l'IA.
// 2. `regime` est le RÉGLAGE D'AUJOURD'HUI (`regime_basis: 'current_config'`), pas
//    l'historique. L'onglet Suivi affiche la même valeur technique pour le régime AU MOMENT
//    DE CHAQUE DÉCISION. D'où deux libellés d'écran distincts (D15) : ici « Réglage actuel ».
//
// ⚠ Contrat de montage : le conteneur rend `<SitesConfig />` SANS AUCUNE PROP, en
//   `{tab === 'sites' && …}` — le composant est donc démonté à chaque changement d'onglet et
//   re-tire CD2 à chaque retour. C'est sain (garde `_isMounted`), ne pas ajouter de cache.
//   Les props partagées (période, export) n'arriveront qu'avec B3_01.
// ⚠ Class component sans hooks — convention du repo. Coquille et jauge calquées sur
//   Funnel.js, patron de table sur DeactivationFeedback.js:242-265.

const nf = new Intl.NumberFormat('fr-FR');

// Surfaces réellement analysées par le moteur (D6) — PAS la copie des cases du back-office.
//
// ⛔ `comment_widget` est codé en dur à `true` côté endpoint : les commentaires du widget sont
//    analysés dès que l'IA est active, quel que soit `modere.comment` (P1 D9). L'afficher
//    comme une case cochée laisserait croire à un réglage modifiable qui n'existe pas — d'où
//    le drapeau `always` et un rendu distinct.
const SURFACES = [
  { key: 'comment_widget', label: 'Commentaires du widget', always: true },
  { key: 'page_comment', label: 'Commentaires de sujet' },
  { key: 'page_comment_group', label: 'Commentaires de groupe' },
  { key: 'inbox', label: 'Messagerie privée' },
  { key: 'page', label: 'Sujets GraphDebate' },
  { key: 'page_group', label: 'Sujets de groupe' },
];

// Surfaces GraphDebate d'un site — décide si le lien « forum » a un sens (décision L3).
// ⛔ Ce test n'est possible QUE sur cet onglet : CD2 renvoie `surfaces`, CD1 (onglet Suivi) non.
// C'est la raison pour laquelle le lien forum ne vit pas sur l'autre onglet.
const FORUM_SURFACES = ['page', 'page_group', 'page_comment', 'page_comment_group'];
function hasForumSurface(site) {
  const s = (site && site.surfaces) || {};
  return FORUM_SURFACES.some((k) => s[k] === true);
}

const MODE_FILTER_ALL = 'ALL';
const SORT_VOLUME = 'volume'; // = l'ordre renvoyé par l'endpoint
const SORT_NAME = 'name';
const SORT_USAGE = 'usage';

// Volume analysé depuis toujours (les deux moitiés du prédicat « a consommé »).
// ⚠ Cumul SANS fenêtre temporelle : ne jamais l'additionner au champ `analyses` de CD1, qui
//   est fenêtré (90 j par défaut). Les deux ne comptent pas la même période.
function totalVerdicts(site) {
  const a = site.activity || {};
  return (Number(a.comment_verdicts) || 0) + (Number(a.page_verdicts) || 0);
}

class SitesConfig extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      isLoading: false,
      error: null,
      expanded: {}, // website_id → contexte éditorial déplié (D12 : replié par défaut)
      modeFilter: MODE_FILTER_ALL,
      sort: SORT_VOLUME,
    };
    this.fetchSites = this.fetchSites.bind(this);
    this.onRefresh = this.onRefresh.bind(this);
    this.toggleContext = this.toggleContext.bind(this);
    this.handleModeFilter = this.handleModeFilter.bind(this);
    this.handleSort = this.handleSort.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchSites();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Lecture seule : tout vient de B1_02, l'écran affiche, il n'agrège pas.
  fetchSites() {
    this.setState({ isLoading: true, error: null });
    getSites()
      .then((result) => {
        if (!this._isMounted) return;
        // ⚠ GARDE DÉFENSIVE — http.get ne lève que sur un statut non-2xx (services/http.js:9),
        // or l'API répond « HTTP 200 + { gcCode: 410 } » quand le JWT est absent ou expiré.
        // Sans ce test, un token expiré entre le montage et un « Rafraîchir » arriverait ici
        // COMME DES DONNÉES : l'écran dirait « aucun site » et un pied à zéro, c'est-à-dire
        // « l'IA ne tourne nulle part », au lieu de dire que la session est tombée.
        // withAuth ne couvre que le montage, pas les recharges. Même garde que Monitoring.jsx.
        // ⚠ `err !== 'undefined'` : l'API renvoie parfois la CHAÎNE littérale 'undefined' dans
        // ce champ (précédent withAuth.jsx:34 et App.js:61).
        const hasErr = result && result.err && result.err !== 'undefined';
        if (!result || typeof result !== 'object' || result.gcCode || hasErr) {
          const code = result && (result.gcCode || result.err);
          const msg = (result && result.msg) ? ` — ${result.msg}` : '';
          this.setState({
            error: `session expirée ou accès refusé (code ${code})${msg}. Recharge la page pour te reconnecter.`,
            isLoading: false,
          });
          return;
        }
        this.setState({ data: result, isLoading: false });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({ error: (err && err.message) || 'Erreur de chargement', isLoading: false });
      });
  }

  onRefresh(e) {
    if (e && e.preventDefault) e.preventDefault();
    this.fetchSites();
  }

  toggleContext(websiteId) {
    this.setState((prev) => ({
      expanded: { ...prev.expanded, [websiteId]: !prev.expanded[websiteId] },
    }));
  }

  handleModeFilter(e) {
    this.setState({ modeFilter: e.target.value });
  }

  handleSort(e) {
    this.setState({ sort: e.target.value });
  }

  // Tri/filtre LOCAUX et ADDITIONNELS.
  // ⛔ L'ordre par défaut reste celui de l'endpoint (volume analysé décroissant puis nom) :
  //    le réappliquer d'office écraserait un classement métier voulu côté serveur.
  // ⚠ Ils portent sur la liste REÇUE, qui peut être tronquée (cf. bandeau `truncated_list`) :
  //    un filtre qui ne rend rien ne prouve pas qu'aucun site ne correspond.
  visibleSites() {
    const { data, modeFilter, sort } = this.state;
    const sites = (data && data.sites) || [];
    const filtered = modeFilter === MODE_FILTER_ALL
      ? sites
      : sites.filter((s) => s.llm_ai_type === modeFilter);

    if (sort === SORT_VOLUME) return filtered; // ordre de l'endpoint, laissé intact
    const copy = filtered.slice();
    if (sort === SORT_NAME) {
      copy.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    } else if (sort === SORT_USAGE) {
      copy.sort((a, b) => (Number(b.usage && b.usage.count) || 0) - (Number(a.usage && a.usage.count) || 0));
    }
    return copy;
  }

  // Colonne « État » = axes 1 et 3 (armement + statut du site). L'axe 2 (réglage actuel) vit
  // dans SA colonne, sous l'en-tête qui le nomme — cf. renderRegimeBadge.
  // ⛔ Les trois axes ne sont JAMAIS fusionnés en un seul badge (cf. en-tête de fichier).
  renderStateBadges(site) {
    const badges = [];

    // Axe 1 — armement. Le seul signal d'actualité (`active` est toujours vrai, cf. en-tête).
    if (site.armed) {
      badges.push(
        <Chip key="armed" size="small" color="success" variant="outlined" label="IA active" data-testid="sites-config-armed" />
      );
    } else {
      badges.push(
        <Tooltip key="armed" title="Ce site a réellement consommé par le passé, mais la modération IA y est coupée aujourd’hui : le moteur ne l’analyse plus.">
          <Chip size="small" color="default" variant="outlined" icon={<InfoOutlinedIcon />} label="IA coupée" data-testid="sites-config-armed" />
        </Tooltip>
      );
    }

    // Axe 3 — statut du site. Les sites fermés / en attente sont VOLONTAIREMENT inclus par
    // l'endpoint (ils ont consommé, et les exclure ferait diverger le total d'analyses de
    // celui de /verdicts, qui ne filtre pas non plus). Décision Mathias 2026-08-27 : gardés
    // dans la liste, mais marqués — jamais confondus avec un site vivant.
    if (site.status && site.status !== 'validated') {
      badges.push(
        <Tooltip key="status" title="Ce site n’est pas un site validé en activité : il est fermé ou en attente de validation. Il figure ici parce qu’il a réellement consommé de la modération IA.">
          <Chip size="small" color="error" variant="outlined" label={site.status === 'pending' ? 'site en attente' : 'site fermé'} data-testid="sites-config-status" />
        </Tooltip>
      );
    }

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {badges}
      </Stack>
    );
  }

  // Axe 2 — le réglage actuel (D15). Distinct de l'armement : un site peut être armé ET en
  // suggestion, c'est même le cas majoritaire (AI_AND_INTERNAL plafonne la clé, P1 D33).
  // ⛔ Rendu dans la colonne « Mode & réglage actuel » : sous un en-tête qui promet le réglage
  //    actuel, on doit trouver le régime — pas `autonomous`, qui ne suffit pas à le déterminer.
  renderRegimeBadge(site, criteria) {
    return (
      <Tooltip title={(criteria && criteria.regime) || 'Réglage actuel du site — pas le régime au moment des verdicts passés.'}>
        <Chip
          size="small"
          color={site.regime === REGIME_APPLIED ? 'warning' : 'default'}
          variant={site.regime === REGIME_APPLIED ? 'filled' : 'outlined'}
          label={regimeLabel(site.regime)}
          data-testid="sites-config-regime"
        />
      </Tooltip>
    );
  }

  // Contexte éditorial (D12) : extrait DÉPLIABLE, replié par défaut.
  //
  // ⛔ Ne JAMAIS libeller ce bloc « contexte envoyé au modèle ». L'endpoint l'acte dans
  //    `criteria.context_website` : le texte suit trois chemins différents selon la surface
  //    (transmis tel quel aux sujets GraphDebate, préfixé puis raboté pour les commentaires du
  //    widget et la messagerie, JAMAIS transmis aux commentaires de sujet ou de groupe).
  //    Le présenter comme l'entrée du modèle serait la même classe de mensonge que d'afficher
  //    `autonomous` à la place du régime.
  renderContext(site, criteria) {
    const ctx = site.context_website || {};
    const isOpen = !!this.state.expanded[site.website_id];

    if (!ctx.present) {
      return <Typography variant="body2" color="text.secondary">—</Typography>;
    }

    return (
      <Box>
        <Button size="small" onClick={() => this.toggleContext(site.website_id)} sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
          {isOpen ? 'Masquer' : 'Voir'} le contexte ({nf.format(Number(ctx.length) || 0)} car.)
        </Button>
        <Collapse in={isOpen} unmountOnExit>
          <Tooltip title={(criteria && criteria.context_website) || ''}>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Contexte éditorial du site
            </Typography>
          </Tooltip>
          <Typography
            variant="body2"
            component="div"
            sx={{ mt: 0.5, whiteSpace: 'pre-wrap', maxWidth: 420, maxHeight: 220, overflowY: 'auto' }}
          >
            {ctx.excerpt}
          </Typography>
          {ctx.truncated && (
            <Typography variant="caption" color="text.secondary">
              … extrait tronqué à {nf.format(Number(ctx.max_length) || 0)} caractères sur {nf.format(Number(ctx.length) || 0)}.
            </Typography>
          )}
        </Collapse>
      </Box>
    );
  }

  renderSurfaces(site) {
    const s = site.surfaces || {};
    const on = SURFACES.filter((surface) => surface.always || s[surface.key] === true);
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {on.map((surface) => (
          <Chip
            key={surface.key}
            size="small"
            variant="outlined"
            label={surface.always ? `${surface.label} · toujours` : surface.label}
            color={surface.always ? 'info' : 'default'}
          />
        ))}
      </Stack>
    );
  }

  // Jauge usage / quota.
  // ⛔ Tester `unlimited` D'ABORD : `quota` reste un Number arbitraire dans ce cas (jamais
  //    Infinity, pas JSON-safe), et une jauge y afficherait un pourcentage faux.
  // ℹ Depuis B6_01 (D38) : tout site custom ressort `unlimited:true` (= jamais gaté, ce n'est
  //    plus lié à `ai_allowed`) ; `includedVolume` porte le forfait saisi au super-admin (null =
  //    valeur par défaut `base`, forfait à saisir). Ne jamais déduire du tier.
  // ⚠ `count >= quota` est un état NOMINAL (P1 B1_03) → barre plafonnée à 100 %.
  // ⚠ `usage.count: 0` sur un site qui a beaucoup analysé est nominal aussi : le compteur est
  //   remplacé entier au changement de mois, donc un mois mémorisé antérieur s'affiche 0 sur
  //   le mois courant — pas « pas de données ».
  renderQuota(site, criteria) {
    const quota = site.quota || {};
    const usage = site.usage || {};
    const count = Number(usage.count) || 0;

    if (quota.unlimited === true) {
      // Site sur mesure (B6_01, D46) : jamais gaté, et jamais « illimité » — on affiche les
      // analyses INCLUSES au forfait (quota.includedVolume, saisi sur la licence au super-admin),
      // ou la valeur par défaut tant que le forfait n'est pas saisi. Au-delà : conditions du contrat.
      const included = Number(quota.includedVolume) || 0;
      const shown = included > 0 ? included : Number(quota.base) || 0;
      return (
        <Tooltip title={(criteria && criteria.usage) || ''}>
          <Box>
            <Typography variant="body2">
              {nf.format(count)} / {nf.format(shown)} analyses
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}({usage.month || '—'})
              </Typography>
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              color={included > 0 ? 'default' : 'warning'}
              label={included > 0 ? `${nf.format(included)} incluses au forfait` : `${nf.format(shown)} par défaut — forfait à saisir`}
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Tooltip>
      );
    }

    const limit = Number(quota.quota) || 0;
    const pct = limit > 0 ? (count / limit) * 100 : 0;
    return (
      <Tooltip title={(criteria && criteria.usage) || ''}>
        <Box sx={{ minWidth: 140 }}>
          <Typography variant="body2">
            {nf.format(count)} / {nf.format(limit)}
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}({usage.month || '—'})
            </Typography>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, pct))}
            color={pct >= 100 ? 'error' : 'primary'}
            sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary">
            offre {quota.plan || '—'}
            {Number(quota.addonCount) > 0 ? ` · ${quota.addonCount} add-on` : ''}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  renderRow(site, criteria) {
    const activity = site.activity || {};
    return (
      <TableRow key={site.website_id} hover data-testid="sites-config-row">
        <TableCell>
          <Typography variant="body2">{site.name || '(sans nom)'}</Typography>
          {site.url && (
            <Link href={site.url} target="_blank" rel="noopener noreferrer" variant="caption" color="text.secondary">
              {site.url}
            </Link>
          )}
          {/* Liens croisés (décisions Mathias L2 et L3, 2026-08-27).
              ⛔ Pas de `#` dans `to` : HashRouter le pose lui-même — un `to={`#/…`}` produirait
              un lien mort (faille déjà payée sur B0_02).
              ⚠ L'espace client a sa PROPRE session : un écran de connexion peut apparaître. */}
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Tooltip title="Ouvrir les commentaires de ce site dans l’espace client. Cet espace a sa propre session : un écran de connexion peut apparaître.">
              <Link
                component={RouterLink}
                to={`/super-admin/website/${site.website_id}/comment`}
                variant="caption"
                underline="hover"
                data-testid="sites-config-link-comments"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
              >
                <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
                Commentaires
              </Link>
            </Tooltip>
            {hasForumSurface(site) && (
              <Tooltip title="Ce site a des surfaces GraphDebate analysées par l’IA : ouvrir son forum.">
                <Link
                  component={RouterLink}
                  to={`/super-admin/website/${site.website_id}/debate`}
                  variant="caption"
                  underline="hover"
                  data-testid="sites-config-link-debate"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  <ForumOutlinedIcon sx={{ fontSize: 14 }} />
                  Forum
                </Link>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell data-testid="sites-config-state">{this.renderStateBadges(site)}</TableCell>
        <TableCell data-testid="sites-config-mode">
          <Typography variant="body2">{modeLabel(site.llm_ai_type)}</Typography>
          <Box sx={{ mt: 0.5 }}>{this.renderRegimeBadge(site, criteria)}</Box>
          {/* `autonomous` en donnée TERTIAIRE seulement : seul `regime` dit ce que l'IA fait
              réellement. Le présenter comme le réglage effectif est le contresens que ce
              projet existe pour supprimer — d'où le libellé qui dit ce qu'il vaut. */}
          <Typography variant="caption" color="text.secondary">
            interrupteur d’autonomie : {site.autonomous ? 'armé' : 'non armé'}
          </Typography>
        </TableCell>
        <TableCell>{this.renderContext(site, criteria)}</TableCell>
        <TableCell>{this.renderSurfaces(site)}</TableCell>
        <TableCell align="right">{this.renderQuota(site, criteria)}</TableCell>
        <TableCell align="right">
          <Tooltip title={(criteria && criteria.activity) || ''}>
            <Typography variant="body2">{nf.format(totalVerdicts(site))}</Typography>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            {nf.format(Number(activity.comment_verdicts) || 0)} comm. · {nf.format(Number(activity.page_verdicts) || 0)} sujets
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  render() {
    const { data, isLoading, error, modeFilter, sort } = this.state;

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" action={<Button size="small" onClick={this.onRefresh}>Réessayer</Button>}>
          Impossible de charger la configuration par site : {error}
        </Alert>
      );
    }

    if (!data) return null;

    const criteria = data.criteria || {};
    const totals = data.totals || {};
    const sites = this.visibleSites();
    const dormant = Number(data.dormant_count) || 0;

    return (
      <Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle1">
            Sites où l’IA a réellement travaillé{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              ({nf.format(sites.length)} affiché{sites.length > 1 ? 's' : ''} sur {nf.format(Number(totals.listed) || 0)})
            </Typography>
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField select size="small" label="Mode" value={modeFilter} onChange={this.handleModeFilter} sx={{ minWidth: 220 }}>
              <MenuItem value={MODE_FILTER_ALL}>Tous les modes</MenuItem>
              {CANONICAL_MODES.map((m) => (
                <MenuItem key={m} value={m}>{modeLabel(m)}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Tri" value={sort} onChange={this.handleSort} sx={{ minWidth: 180 }}>
              <MenuItem value={SORT_VOLUME}>Volume analysé (défaut)</MenuItem>
              <MenuItem value={SORT_USAGE}>Usage du mois</MenuItem>
              <MenuItem value={SORT_NAME}>Nom</MenuItem>
            </TextField>
            <Button size="small" variant="outlined" onClick={this.onRefresh}>Rafraîchir</Button>
          </Stack>
        </Stack>

        {/* ⛔ Ne JAMAIS présenter la liste comme exhaustive sans regarder ce drapeau : une
            troncature muette se lirait « voilà tous les sites ». Et le tri/filtre ci-dessus
            porte alors sur une liste déjà coupée. */}
        {data.truncated_list && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Liste tronquée : {nf.format(Number(totals.active) || 0)} sites correspondent, seuls les{' '}
            {nf.format(Number(totals.limit) || 0)} premiers (par volume analysé) sont affichés. Le tri et le
            filtre ci-dessus ne portent que sur cette portion.
          </Alert>
        )}

        {/* ⛔ « armés par défaut », PAS « configurations posées » (décision Mathias
            2026-08-27). `modere.llm_ai: true` est ÉCRIT à la création de tout nouveau site
            (teaser freemium ; depuis le 2026-09-21 ce n'est plus le défaut du schéma, qui
            armait aussi tout le parc antérieur) : ces sites n'ont rien configuré du tout. Les appeler des configurations volontaires
            ferait lire ce compteur comme un vivier commercial. */}
        {dormant > 0 && (
          <Tooltip title={criteria.dormant || ''}>
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
              + {nf.format(dormant)} site{dormant > 1 ? 's' : ''} armé{dormant > 1 ? 's' : ''} par défaut, jamais utilisé{dormant > 1 ? 's' : ''} —
              l’IA y est activée d’office, ce n’est pas un réglage choisi par le client.
            </Alert>
          </Tooltip>
        )}

        <TableContainer data-testid="sites-config-table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Site</TableCell>
                <TableCell>
                  <Tooltip title={criteria.active || ''}><span>État</span></Tooltip>
                </TableCell>
                {/* D15 — « Réglage actuel », jamais « Régime » nu : sur l'onglet Suivi la même
                    valeur technique désigne le régime au moment de chaque décision. */}
                <TableCell>Mode &amp; réglage actuel</TableCell>
                <TableCell>Contexte éditorial</TableCell>
                <TableCell>
                  <Tooltip title={criteria.surfaces || ''}><span>Surfaces analysées</span></Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={criteria.usage || ''}><span>Usage du mois / quota</span></Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={criteria.activity || ''}><span>Analyses (total)</span></Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      {/* Ne pas accuser le filtre quand il n'y a rien à filtrer : une liste
                          vide côté endpoint (aucun site n'a jamais consommé) et un filtre trop
                          étroit sont deux causes différentes, et la première est une
                          information en soi. */}
                      {((data.sites || []).length === 0)
                        ? 'Aucun site n’a encore consommé de modération IA.'
                        : 'Aucun site ne correspond à ce filtre.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sites.map((site) => this.renderRow(site, criteria))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pied d'arithmétique auditable : armed === dormant_count + active_armed. C'est ce
            que le contrôleur cherchait en exposant `totals` — le vérifier à l'œil vaut mieux
            que de croire trois compteurs sur parole. */}
        <Tooltip title={criteria.totals || ''}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
            {nf.format(Number(totals.websites) || 0)} sites au total · {nf.format(Number(totals.armed) || 0)} armés
            = {nf.format(dormant)} jamais utilisés + {nf.format(Number(totals.active_armed) || 0)} actifs et armés ·
            {' '}{nf.format(Number(totals.active) || 0)} ont consommé, {nf.format(Number(totals.listed) || 0)} listés
          </Typography>
        </Tooltip>

        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Lecture seule. Le mode et le contexte éditorial s’éditent dans le back-office du client ;
          l’autonomie et l’illimité Enterprise, dans la fiche de la communauté.
        </Typography>
      </Box>
    );
  }
}

export default SitesConfig;
